import { useCallback, useEffect, useRef, useState } from "react";
import API_BASE, { withApiCredentials } from "../../api";
import { trackProductEvent } from "../../analytics/trackEvent";
import { readErrorDetail } from "./GroupsSharedUi";

export default function useGroupPostsState({
  activeGroupHome,
  activeGroupHomeId,
  groups,
  token,
  user,
  userId,
  t,
}) {
  const [groupPosts, setGroupPosts] = useState([]);
  const [groupPostsLoading, setGroupPostsLoading] = useState(false);
  const [groupPostsError, setGroupPostsError] = useState("");
  const [pendingGroupPosts, setPendingGroupPosts] = useState([]);
  const [pendingGroupPostsLoading, setPendingGroupPostsLoading] =
    useState(false);
  const [pendingGroupPostsError, setPendingGroupPostsError] = useState("");
  const [isGroupPostComposerOpen, setIsGroupPostComposerOpen] = useState(false);
  const [groupPostCaption, setGroupPostCaption] = useState("");
  const [groupPostFiles, setGroupPostFiles] = useState([]);
  const [groupPostMediaPreviews, setGroupPostMediaPreviews] = useState([]);
  const [groupPostSubmitting, setGroupPostSubmitting] = useState(false);
  const [groupPostError, setGroupPostError] = useState("");
  const [groupPostNotice, setGroupPostNotice] = useState("");
  const [groupPostModerationLoadingKey, setGroupPostModerationLoadingKey] =
    useState("");
  const [groupPostModerationError, setGroupPostModerationError] = useState("");
  const [openGroupPostCommentsId, setOpenGroupPostCommentsId] = useState(null);
  const [groupPostCommentDrafts, setGroupPostCommentDrafts] = useState({});
  const [groupPostLikeLoadingId, setGroupPostLikeLoadingId] = useState("");
  const [groupPostCommentSubmittingId, setGroupPostCommentSubmittingId] =
    useState("");
  const [groupPostInteractionErrorById, setGroupPostInteractionErrorById] =
    useState({});
  const groupPostComposerRef = useRef(null);
  const groupPostMediaInputRef = useRef(null);

  const resetPendingGroupPostsState = useCallback(() => {
    setPendingGroupPosts([]);
    setPendingGroupPostsLoading(false);
    setPendingGroupPostsError("");
    setGroupPostModerationLoadingKey("");
    setGroupPostModerationError("");
  }, []);

  const resetGroupPostInteractionState = useCallback(() => {
    setOpenGroupPostCommentsId(null);
    setGroupPostCommentDrafts({});
    setGroupPostLikeLoadingId("");
    setGroupPostCommentSubmittingId("");
    setGroupPostInteractionErrorById({});
  }, []);

  const clearGroupPostMediaSelection = useCallback(() => {
    setGroupPostFiles([]);
    if (groupPostMediaInputRef.current) {
      groupPostMediaInputRef.current.value = "";
    }
  }, []);

  const toggleGroupPostComposer = useCallback(() => {
    setIsGroupPostComposerOpen((prev) => !prev);
    setGroupPostError("");
    setGroupPostNotice("");
  }, []);

  const cancelGroupPostComposer = useCallback(() => {
    setIsGroupPostComposerOpen(false);
    setGroupPostError("");
  }, []);

  const loadGroupPosts = useCallback(
    async (groupId) => {
      if (!user || !groupId) {
        setGroupPosts([]);
        setGroupPostsLoading(false);
        setGroupPostsError("");
        return;
      }

      setGroupPostsLoading(true);
      setGroupPostsError("");

      try {
        const response = await fetch(
          `${API_BASE}/groups/${groupId}/posts`,
          withApiCredentials({
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
        );

        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(readErrorDetail(data, t("groups_posts_load_failed")));
        }

        setGroupPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        setGroupPostsError(err.message || t("groups_posts_load_failed"));
      } finally {
        setGroupPostsLoading(false);
      }
    },
    [t, token, user]
  );

  const loadPendingGroupPosts = useCallback(
    async (groupId) => {
      if (!userId || !groupId) {
        resetPendingGroupPostsState();
        return;
      }

      setPendingGroupPostsLoading(true);
      setPendingGroupPostsError("");

      try {
        const response = await fetch(
          `${API_BASE}/groups/${groupId}/posts/pending`,
          withApiCredentials({
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
        );

        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(
            readErrorDetail(
              data,
              t("groups_pending_posts_load_failed", {
                defaultValue: "Could not load pending group posts.",
              })
            )
          );
        }

        setPendingGroupPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        setPendingGroupPostsError(
          err.message ||
            t("groups_pending_posts_load_failed", {
              defaultValue: "Could not load pending group posts.",
            })
        );
      } finally {
        setPendingGroupPostsLoading(false);
      }
    },
    [resetPendingGroupPostsState, t, token, userId]
  );

  const upsertGroupPostIntoFeed = useCallback((updatedPost) => {
    if (!updatedPost?.id) return;

    setGroupPosts((prevPosts) => {
      const existingPostIndex = prevPosts.findIndex(
        (post) => post.id === updatedPost.id
      );
      if (existingPostIndex === -1) {
        return [updatedPost, ...prevPosts];
      }

      return prevPosts.map((post) =>
        post.id === updatedPost.id ? updatedPost : post
      );
    });
  }, []);

  const prepareOpenGroupHome = useCallback(() => {
    setGroupPostCaption("");
    clearGroupPostMediaSelection();
    setGroupPostError("");
    setGroupPostNotice("");
    setGroupPostModerationError("");
    setGroupPostModerationLoadingKey("");
    resetGroupPostInteractionState();
  }, [clearGroupPostMediaSelection, resetGroupPostInteractionState]);

  const resetGroupHomeState = useCallback(() => {
    setGroupPosts([]);
    setGroupPostsError("");
    resetPendingGroupPostsState();
    setIsGroupPostComposerOpen(false);
    setGroupPostCaption("");
    clearGroupPostMediaSelection();
    setGroupPostError("");
    setGroupPostNotice("");
    resetGroupPostInteractionState();
  }, [
    clearGroupPostMediaSelection,
    resetGroupPostInteractionState,
    resetPendingGroupPostsState,
  ]);

  useEffect(() => {
    if (!activeGroupHomeId) {
      setGroupPosts([]);
      setGroupPostsError("");
      setGroupPostsLoading(false);
      return;
    }

    const group = groups.find((item) => item.id === activeGroupHomeId);
    if (!group || !group.members.includes(user?.id)) {
      setGroupPosts([]);
      setGroupPostsError("");
      setGroupPostsLoading(false);
      return;
    }

    loadGroupPosts(activeGroupHomeId);
  }, [activeGroupHomeId, groups, loadGroupPosts, user?.id]);

  useEffect(() => {
    if (!activeGroupHomeId) {
      resetPendingGroupPostsState();
      return;
    }

    const group = groups.find((item) => item.id === activeGroupHomeId);
    if (!group || !group.members.includes(user?.id)) {
      resetPendingGroupPostsState();
      return;
    }

    if (!group.admins.includes(user?.id)) {
      resetPendingGroupPostsState();
      return;
    }

    loadPendingGroupPosts(activeGroupHomeId);
  }, [
    activeGroupHomeId,
    groups,
    loadPendingGroupPosts,
    resetPendingGroupPostsState,
    user?.id,
  ]);

  useEffect(() => {
    if (!activeGroupHomeId || !isGroupPostComposerOpen) return;

    groupPostComposerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
    groupPostComposerRef.current?.querySelector("textarea")?.focus();
  }, [activeGroupHomeId, isGroupPostComposerOpen]);

  useEffect(() => {
    if (!groupPostFiles.length) {
      setGroupPostMediaPreviews([]);
      return;
    }

    const nextPreviews = groupPostFiles.map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${index}`,
      url: URL.createObjectURL(file),
      mediaType: file.type.startsWith("video/") ? "video" : "image",
      name: file.name,
    }));

    setGroupPostMediaPreviews(nextPreviews);

    return () => {
      nextPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [groupPostFiles]);

  function handleGroupPostMediaChange(event) {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      return;
    }

    const supportedFiles = selectedFiles.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );

    if (!supportedFiles.length) {
      setGroupPostError(
        t("groups_post_media_invalid", {
          defaultValue: "Only image and video files are supported.",
        })
      );
      if (groupPostMediaInputRef.current) {
        groupPostMediaInputRef.current.value = "";
      }
      return;
    }

    setGroupPostFiles((prevFiles) => [...prevFiles, ...supportedFiles]);
    setGroupPostError(
      supportedFiles.length === selectedFiles.length
        ? ""
        : t("groups_post_media_invalid", {
            defaultValue: "Only image and video files are supported.",
          })
    );

    if (groupPostMediaInputRef.current) {
      groupPostMediaInputRef.current.value = "";
    }
  }

  function removeGroupPostMedia(indexToRemove) {
    setGroupPostFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  }

  async function handleCreateGroupPost(event) {
    event.preventDefault();

    if (!user || !activeGroupHome) return;

    const activeGroupHomeIdForSubmit = activeGroupHome.id;
    const trimmedCaption = groupPostCaption.trim();
    if (!trimmedCaption && groupPostFiles.length === 0) {
      setGroupPostError(
        t("groups_post_content_required", {
          defaultValue: "Add a caption or attach media before posting.",
        })
      );
      return;
    }

    setGroupPostSubmitting(true);
    setGroupPostError("");
    setGroupPostNotice("");

    try {
      const formData = new FormData();
      formData.append("caption", trimmedCaption);
      groupPostFiles.forEach((file) => {
        formData.append("media", file);
      });

      const response = await fetch(
        `${API_BASE}/groups/${activeGroupHomeIdForSubmit}/posts`,
        withApiCredentials({
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_post_create_failed")));
      }

      if (data?.status === "pending") {
        setGroupPostNotice(
          t("groups_post_pending_notice", {
            defaultValue: "Your post was submitted for admin approval.",
          })
        );
      } else {
        upsertGroupPostIntoFeed(data);
        setGroupPostNotice("");
      }
      trackProductEvent("group_post_created", {
        featureArea: "groups",
        metadata: {
          source: "group_home_composer",
          status: data?.status === "pending" ? "pending" : "published",
          has_media: groupPostFiles.length > 0,
          media_count_bucket:
            groupPostFiles.length === 0
              ? "0"
              : groupPostFiles.length === 1
                ? "1"
                : groupPostFiles.length <= 3
                  ? "2-3"
                  : "4+",
        },
      });
      setGroupPostCaption("");
      clearGroupPostMediaSelection();
      setIsGroupPostComposerOpen(false);
    } catch (err) {
      setGroupPostError(err.message || t("groups_post_create_failed"));
    } finally {
      setGroupPostSubmitting(false);
    }
  }

  function toggleGroupPostComments(postId) {
    setOpenGroupPostCommentsId((prevPostId) =>
      prevPostId === postId ? null : postId
    );
    setGroupPostInteractionErrorById((prevErrors) => ({
      ...prevErrors,
      [postId]: "",
    }));
  }

  function handleGroupPostCommentDraftChange(postId, value) {
    setGroupPostCommentDrafts((prevDrafts) => ({
      ...prevDrafts,
      [postId]: value,
    }));
    setGroupPostInteractionErrorById((prevErrors) => ({
      ...prevErrors,
      [postId]: "",
    }));
  }

  async function handleToggleGroupPostLike(postId) {
    if (!user || !activeGroupHome?.id || !postId) return false;

    setGroupPostLikeLoadingId(postId);
    setGroupPostInteractionErrorById((prevErrors) => ({
      ...prevErrors,
      [postId]: "",
    }));

    try {
      const response = await fetch(
        `${API_BASE}/groups/${activeGroupHome.id}/posts/${postId}/like`,
        withApiCredentials({
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          readErrorDetail(
            data,
            t("groups_post_like_failed", {
              defaultValue: "Could not update the like on that group post.",
            })
          )
        );
      }

      upsertGroupPostIntoFeed(data);
      return true;
    } catch (err) {
      setGroupPostInteractionErrorById((prevErrors) => ({
        ...prevErrors,
        [postId]:
          err.message ||
          t("groups_post_like_failed", {
            defaultValue: "Could not update the like on that group post.",
          }),
      }));
      return false;
    } finally {
      setGroupPostLikeLoadingId("");
    }
  }

  async function handleSubmitGroupPostComment(postId, event) {
    event.preventDefault();

    if (!user || !activeGroupHome?.id || !postId) return false;

    const trimmedComment = (groupPostCommentDrafts[postId] || "").trim();
    if (!trimmedComment) {
      setGroupPostInteractionErrorById((prevErrors) => ({
        ...prevErrors,
        [postId]: t("groups_post_comment_required", {
          defaultValue: "Write a comment before posting it.",
        }),
      }));
      return false;
    }

    setGroupPostCommentSubmittingId(postId);
    setGroupPostInteractionErrorById((prevErrors) => ({
      ...prevErrors,
      [postId]: "",
    }));

    try {
      const response = await fetch(
        `${API_BASE}/groups/${activeGroupHome.id}/posts/${postId}/comments`,
        withApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ text: trimmedComment }),
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          readErrorDetail(
            data,
            t("groups_post_comment_failed", {
              defaultValue: "Could not comment on that group post.",
            })
          )
        );
      }

      upsertGroupPostIntoFeed(data);
      setGroupPostCommentDrafts((prevDrafts) => ({
        ...prevDrafts,
        [postId]: "",
      }));
      setOpenGroupPostCommentsId(postId);
      return true;
    } catch (err) {
      setGroupPostInteractionErrorById((prevErrors) => ({
        ...prevErrors,
        [postId]:
          err.message ||
          t("groups_post_comment_failed", {
            defaultValue: "Could not comment on that group post.",
          }),
      }));
      return false;
    } finally {
      setGroupPostCommentSubmittingId("");
    }
  }

  async function handleModerateGroupPost(postId, action) {
    if (!user || !activeGroupHome?.id || !postId) return false;

    const fallbackError =
      action === "approve"
        ? t("groups_post_approve_failed", {
            defaultValue: "Could not approve that group post.",
          })
        : t("groups_post_reject_failed", {
            defaultValue: "Could not reject that group post.",
          });

    setGroupPostModerationLoadingKey(`${action}:${postId}`);
    setGroupPostModerationError("");

    try {
      const response = await fetch(
        `${API_BASE}/groups/${activeGroupHome.id}/posts/${postId}/${action}`,
        withApiCredentials({
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, fallbackError));
      }

      setPendingGroupPosts((prevPosts) =>
        prevPosts.filter((post) => post.id !== postId)
      );
      if (action === "approve") {
        upsertGroupPostIntoFeed(data);
      }

      window.dispatchEvent(new Event("groupsUpdated"));
      return true;
    } catch (err) {
      setGroupPostModerationError(err.message || fallbackError);
      return false;
    } finally {
      setGroupPostModerationLoadingKey("");
    }
  }

  return {
    groupPosts,
    groupPostsLoading,
    groupPostsError,
    pendingGroupPosts,
    pendingGroupPostsLoading,
    pendingGroupPostsError,
    isGroupPostComposerOpen,
    groupPostCaption,
    setGroupPostCaption,
    groupPostFiles,
    groupPostMediaPreviews,
    groupPostSubmitting,
    groupPostError,
    groupPostNotice,
    groupPostModerationLoadingKey,
    groupPostModerationError,
    openGroupPostCommentsId,
    groupPostCommentDrafts,
    groupPostLikeLoadingId,
    groupPostCommentSubmittingId,
    groupPostInteractionErrorById,
    groupPostComposerRef,
    groupPostMediaInputRef,
    resetPendingGroupPostsState,
    resetGroupPostInteractionState,
    clearGroupPostMediaSelection,
    toggleGroupPostComposer,
    cancelGroupPostComposer,
    prepareOpenGroupHome,
    resetGroupHomeState,
    handleCreateGroupPost,
    toggleGroupPostComments,
    handleGroupPostCommentDraftChange,
    handleToggleGroupPostLike,
    handleSubmitGroupPostComment,
    handleModerateGroupPost,
    handleGroupPostMediaChange,
    removeGroupPostMedia,
  };
}
